import { ListTodo } from "lucide-react";

import { AgentPromptInput } from "@/components/messages/Omnibox/AgentPromptInput.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import type { UserTask } from "@/types/userTasks.ts";
import { useSetUserTaskStatus } from "./api.ts";

export const START_TASK_PLACEHOLDER =
  "What should happen with this task? Add any context okthx should know";

/**
 * Start a task: a composer with the task attached, where the user says what
 * they want done and adds what okthx should know. Sending opens a new thread
 * linked to the task, in the background: the user stays on the list, where the
 * row picks the thread up. Centered on a wide screen, the whole screen on a
 * phone.
 *
 * Mounted only while open, so the field starts empty every time: a draft
 * abandoned by closing was a decision.
 */
export const StartTaskDialog = ({ task, onClose }: { task: UserTask; onClose: () => void }) => {
  const setStatus = useSetUserTaskStatus();
  // Starting a snoozed task is taking it up again, so it comes back to Open.
  const onSubmitted = () => {
    if (task.status === "later") {
      setStatus.mutate({ taskId: task.id, fromStatus: "later", status: "open", resumeWork: false });
    }
    onClose();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-md:inset-0 max-md:flex max-md:h-dvh max-md:max-w-none max-md:translate-x-0 max-md:translate-y-0 max-md:flex-col max-md:rounded-none max-md:border-0">
        <DialogHeader>
          <DialogTitle>Start this task</DialogTitle>
          <DialogDescription>
            Okthx works on it in a new thread linked to the task.
          </DialogDescription>
        </DialogHeader>
        <AgentPromptInput
          placeholder={START_TASK_PLACEHOLDER}
          chipsSlot={<TaskChip title={task.title} />}
          taskContext={{ taskId: task.id }}
          viewContext={{ canvasItem: task.urn }}
          hostsPageEnrichments={false}
          alwaysInBackground
          onEscape={onClose}
          onSubmitted={onSubmitted}
        />
      </DialogContent>
    </Dialog>
  );
};

/** The task the dialog starts, shown inside the composer as what the message is about. */
const TaskChip = ({ title }: { title: string }) => (
  <span className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full border bg-muted px-2.5 py-1 text-xs">
    <ListTodo className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
    <span className="truncate">{title}</span>
  </span>
);
